import { Fragment } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn";

export interface Crumb {
  label: string;
  /** Omitted on the final crumb — the page you are already on is not a link. */
  onClick?: () => void;
}

/**
 * The trail into a drill-down view, so "how did I get here" is never guesswork.
 *
 * Rendered as a <nav> with an ordered list because the sequence is meaningful, and marked
 * aria-current="page" on the last crumb so assistive tech announces which one you are on.
 * Separators are aria-hidden — a screen reader should hear "Messaging, Sent, Message detail",
 * not a chevron between each.
 */
export function Breadcrumb({ items, className }: { items: Crumb[]; className?: string }) {
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
      <ol className="flex flex-wrap items-center gap-1 text-xs text-text-muted">
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <Fragment key={`${item.label}-${i}`}>
              <li className="min-w-0">
                {last || !item.onClick ? (
                  <span
                    aria-current={last ? "page" : undefined}
                    className={cn("truncate", last && "font-medium text-text")}
                  >
                    {item.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={item.onClick}
                    className={cn(
                      "truncate rounded-control px-1 -mx-1 transition-colors duration-fast ease-standard",
                      "hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                    )}
                  >
                    {item.label}
                  </button>
                )}
              </li>
              {!last && (
                <li aria-hidden className="flex items-center">
                  <ChevronRight className="size-3 shrink-0 text-text-muted/60" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
